'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import type { ResourceDto } from '@vuganawe/types';
import { getResourceWithAuth } from '@/lib/api/public';
import { getLearnerToken } from '@/lib/learnerSession';

function paragraphs(body: string | null, fallback: string): string[] {
  const text = (body ?? '').trim() || fallback;
  return text.split('\n').filter(Boolean);
}

/** Render the type-specific attachment (video / audio / worksheet / image). */
function ResourceMedia({ url, mimeType }: { url: string; mimeType: string | null }) {
  const mime = mimeType ?? '';
  if (mime.startsWith('video/')) {
    return (
      <video
        src={url}
        controls
        style={{ width: '100%', maxWidth: 720, borderRadius: 'var(--radius-md)', background: '#000' }}
      />
    );
  }
  if (mime.startsWith('audio/')) {
    return <audio src={url} controls style={{ width: '100%', maxWidth: 480 }} />;
  }
  if (mime === 'application/pdf') {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="vn-btn vn-btn--primary">
        Download worksheet (PDF)
      </a>
    );
  }
  if (mime.startsWith('image/')) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={url} alt="" style={{ width: '100%', maxWidth: 720, borderRadius: 'var(--radius-md)' }} />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="vn-btn vn-btn--primary">
      Open attachment
    </a>
  );
}

export function ResourceAccessPanel({ resource }: { resource: ResourceDto }) {
  const isGated = resource.accessLevel === 'ACCOUNT';
  const [activeResource, setActiveResource] = useState(resource);
  const [unlocked, setUnlocked] = useState(!isGated);
  // Until we've checked localStorage we don't know if the visitor can unlock.
  const [checking, setChecking] = useState(isGated);

  useEffect(() => {
    if (!isGated) return;
    const token = getLearnerToken();
    if (!token) {
      setChecking(false);
      return;
    }
    void getResourceWithAuth(resource.slug, token)
      .then((full) => {
        setActiveResource(full);
        setUnlocked(true);
      })
      .catch(() => {
        // Token invalid/expired — fall back to the preview + CTA.
      })
      .finally(() => setChecking(false));
  }, [isGated, resource.slug]);

  const loginHref = `/account/login?next=${encodeURIComponent(`/resources/${resource.slug}`)}`;
  const registerHref = `/account/register?next=${encodeURIComponent(`/resources/${resource.slug}`)}`;

  const mediaUrl = unlocked ? activeResource.mediaUrl : resource.mediaUrl;
  const mediaMimeType = unlocked ? activeResource.mediaMimeType : resource.mediaMimeType;

  return (
    <div className="vn-prose">
      <h2>The activity</h2>
      {mediaUrl ? (
        <div style={{ margin: 'var(--space-4) 0' }}>
          <ResourceMedia url={mediaUrl} mimeType={mediaMimeType} />
        </div>
      ) : null}
      {paragraphs(
        unlocked ? activeResource.body : resource.previewBody,
        unlocked
          ? 'Full instructions for this activity are coming soon.'
          : 'This is a member-only resource. Create a free account to read the full activity.',
      ).map((paragraph) => (
        <p key={paragraph.slice(0, 24)}>{paragraph}</p>
      ))}

      {isGated && !unlocked && !checking ? (
        <div
          style={{
            marginTop: 'var(--space-5)',
            maxWidth: 560,
            padding: 'var(--space-5)',
            border: '1px solid var(--vn-line)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--vn-white)',
          }}
        >
          <h3 style={{ marginTop: 0 }}>Member-only resource</h3>
          <p style={{ color: 'var(--vn-text-soft)' }}>
            Create a free Vuganawe account to unlock the full activity. Already a member? Just sign
            in — it unlocks automatically.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Link href={registerHref} className="vn-btn vn-btn--primary">
              Create free account
            </Link>
            <Link href={loginHref} className="vn-btn vn-btn--secondary">
              Sign in
            </Link>
          </div>
        </div>
      ) : null}

      {isGated && unlocked ? (
        <p style={{ color: 'var(--vn-text-soft)', fontSize: 'var(--text-sm)' }}>
          ✓ Unlocked with your Vuganawe account.
        </p>
      ) : null}
    </div>
  );
}
